// src/views/adminView/contractsView/EmpresaCard.jsx
// Tarjeta de empresa para el tab "Directorio" — presentación tipo mockup
// "Empresas". El click dispara onView(contract) (el detalle inline existente),
// NO enruta. El conteo de pedidos (últimos 30 días) sale de useClientVolume30d
// (una sola llamada para toda la cartera, compartida entre tarjetas).
import { useTranslation } from "react-i18next";
import { ArrowRight, Ban, Clock, MapPin, Package } from "lucide-react";
import { cn } from "../../../utils/cn";
import { Card } from "../../../components/ui/Card";
import { Avatar } from "../../../components/ui/Avatar";
import { Badge } from "../../../components/ui/Badge";
import { CumplimientoChip } from "./compliance/ComplianceShared";
import { formatNumberCL } from "../../../utils/formatters";
import { normalizeSearchText } from "../../../utils/commerceSearch";
import useClientVolume30d from "../../../hooks/useClientVolume30d";

// Iniciales (máx 2) del nombre de la empresa para el avatar.
const initialsOf = (n) =>
  ((n || "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase();

export function EmpresaCard({ contract, onView }) {
  const { t } = useTranslation();
  const c = contract.companyId || {};
  const blocked = contract.paymentBlock?.isBlocked;
  const { loading: volumeLoading, countOf } = useClientVolume30d();
  const orders30d = countOf(c._id);

  const statusMap = {
    active: { tone: "success", label: t("Activo") },
    inactive: { tone: "neutral", label: t("Inactivo") },
    pending: { tone: "warning", label: t("Pendiente") },
  };
  const status = statusMap[contract.status] || statusMap.inactive;

  // Razón social bajo el nombre solo si aporta algo: si repite el nombre (sin
  // distinguir mayúsculas, tildes ni espacios sobrantes) sería una línea
  // redundante.
  const legalName = typeof c.legalName === "string" ? c.legalName.trim() : "";
  const showLegalName = legalName !== "" && normalizeSearchText(legalName) !== normalizeSearchText(c.name);

  // Contacto principal solo si viene en la data (no se inventa).
  const primaryContact = c.contacts?.primary?.name;

  // Badge de vigencia SOLO si el backend ya trae validityPeriod.endDate en la
  // lista; no hacemos getOneContract por tarjeta.
  const endDate = contract.validityPeriod?.endDate;
  let validity = null;
  if (endDate) {
    const vigente = new Date(endDate).getTime() >= Date.now();
    validity = vigente
      ? { tone: "success", label: t("Vigente") }
      : { tone: "neutral", label: t("Terminado") };
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onView(contract);
    }
  };

  return (
    <Card
      padding="md"
      role="button"
      tabIndex={0}
      onClick={() => onView(contract)}
      onKeyDown={handleKeyDown}
      aria-label={`${t("Empresa")} ${c.name || t("Sin nombre")}`}
      className={cn(
        "group cursor-pointer transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        blocked
          ? "border-status-error/40 bg-status-error-soft/40 hover:border-status-error"
          : "hover:border-brand-500 hover:shadow-card",
      )}
    >
      {/* Cabecera: identidad + estado */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar size="md" tone={blocked ? "warning" : "brand"} initials={initialsOf(c.name)} alt={c.name} />
          <div className="min-w-0">
            <div className="text-base font-semibold text-fg truncate">{c.name || t("Sin nombre")}</div>
            {showLegalName && (
              <div className="text-xs text-fg-muted truncate" title={legalName}>
                {legalName}
              </div>
            )}
            <div className="text-xs font-mono text-fg-muted truncate">{c.businessIdentifier || t("Sin RUT")}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
          {blocked && (
            <Badge tone="error">
              <Ban className="w-3 h-3" aria-hidden="true" />
              {t("Bloqueado")}
            </Badge>
          )}
        </div>
      </div>

      {/* Cuerpo: datos reales de la lista. `font-normal` en cada <dt>:
          el reboot de Bootstrap (importado global en App.js) pone
          `dt { font-weight: 700 }` e invertía la jerarquía label/valor. */}
      <dl className="mt-4 space-y-1.5 text-sm">
        {primaryContact && (
          <div className="flex justify-between gap-2">
            <dt className="font-normal text-fg-muted">{t("Contacto")}</dt>
            <dd className="text-fg truncate ml-2">{primaryContact}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="inline-flex items-center gap-1.5 font-normal text-fg-muted">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Retiro")}
          </dt>
          <dd className="ml-2 text-fg">{c.pickUpTime || "—"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="inline-flex items-center gap-1.5 font-normal text-fg-muted">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Direcciones")}
          </dt>
          <dd className="ml-2 text-fg tabular-nums">{c.shippingAddresses?.length || 0}</dd>
        </div>
        {validity && (
          <div className="flex items-center justify-between gap-2">
            <dt className="font-normal text-fg-muted">{t("Contrato")}</dt>
            <dd className="ml-2">
              <Badge tone={validity.tone}>{validity.label}</Badge>
            </dd>
          </div>
        )}
      </dl>

      {/* Pie: semáforo de cumplimiento (front-only) + pedidos últimos 30 días
          + affordance de detalle */}
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CumplimientoChip contract={contract} />
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-bg-muted px-2.5 py-1 text-xs font-medium text-fg-muted"
            title={t("Pedidos creados en los últimos 30 días")}
          >
            <Package className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span className="tabular-nums font-semibold text-fg">
              {volumeLoading ? "…" : orders30d === null ? "—" : formatNumberCL(orders30d)}
            </span>
            {t("pedidos · 30 días")}
          </span>
        </div>
        <ArrowRight
          className="w-4 h-4 shrink-0 text-fg-subtle transition-all group-hover:translate-x-0.5 group-hover:text-brand-500"
          aria-hidden="true"
        />
      </div>
    </Card>
  );
}

export default EmpresaCard;
